/**
 * ============================================================================
 * ESCROW SERVICE - API CLIENT
 * ============================================================================
 * 
 * Handles all escrow-related API calls:
 * - Escrow account management
 * - Fund release operations
 * - Refund processing
 * - Escrow status tracking
 */

import api, { handleApiError } from './api';

/**
 * Get escrow by transaction ID
 */
export const getEscrowByTransaction = async (transactionId) => {
  try {
    const response = await api.get(`/escrows/transaction/${transactionId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get escrow by ID
 */
export const getEscrow = async (escrowId) => {
  try {
    const response = await api.get(`/escrows/${escrowId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Release escrow funds
 */
export const releaseEscrow = async (escrowId, data) => {
  try {
    const response = await api.post(`/escrows/${escrowId}/release`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Refund escrow funds
 */
export const refundEscrow = async (escrowId, data) => {
  try {
    const response = await api.post(`/escrows/${escrowId}/refund`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Partial release escrow funds
 */
export const partialReleaseEscrow = async (escrowId, data) => {
  try {
    const response = await api.post(`/escrows/${escrowId}/partial-release`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Freeze escrow (for disputes)
 */
export const freezeEscrow = async (escrowId) => {
  try {
    const response = await api.post(`/escrows/${escrowId}/freeze`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Unfreeze escrow
 */
export const unfreezeEscrow = async (escrowId) => {
  try {
    const response = await api.post(`/escrows/${escrowId}/unfreeze`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get escrow status
 */
export const getEscrowStatus = async (escrowId) => {
  try {
    const response = await api.get(`/escrows/${escrowId}/status`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * List user escrows
 */
export const listUserEscrows = async (filters = {}) => {
  try {
    const response = await api.get('/escrows/my-escrows', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Create escrow
 */
export const createEscrow = async (data) => {
  try {
    const response = await api.post('/escrows', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Fund escrow
 */
export const fundEscrow = async (escrowId, data) => {
  try {
    const response = await api.post(`/escrows/${escrowId}/fund`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get escrow history
 */
export const getEscrowHistory = async (escrowId) => {
  try {
    const response = await api.get(`/escrows/${escrowId}/history`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export default {
  getEscrowByTransaction,
  getEscrow,
  getEscrowStatus,
  listUserEscrows,
  createEscrow,
  fundEscrow,
  releaseEscrow,
  refundEscrow,
  partialReleaseEscrow,
  freezeEscrow,
  unfreezeEscrow,
  getEscrowHistory,
};
